import { isBefore, startOfDay } from "date-fns";
import { User } from "../user/user.model";
import { Rate } from "./rate-type";
import { rateType } from "./rate-type.const";
import { Carrier } from "../carrier/carrier.model";
import { Client } from "../client/client.model";
import { RateNoteTranslation } from "./rate-note-translation.model";

export class BaseRate {
  constructor(
    protected id: number,
    protected name: string,
    protected discriminator: string,
    protected createdBy?: User,
    protected updatedBy?: User,
    protected createdAt?: Date,
    protected updatedAt?: Date,
    protected type?: string,
    protected source?: string,
    protected status?: string,
    protected internalNote?: string,
    protected externalNote?: string,
    protected pmNote?: string,
    protected comment?: string,
    protected deepLink?: string,
    protected carrier?: Carrier,
    protected validity?: Date[],
    protected contractNumber?: string,
    protected restrictedClients?: Client[],
    protected restrictedUsers?: User[],
    protected rootParent?: Rate,
  ) {}

  protected externalNoteTranslations: RateNoteTranslation[] = [];

  protected agent: Client = null;

  protected availableOnline?: boolean;
  protected defaultForInternalEmail = false;
  protected defaultForExternalEmail = false;
  protected availableForPartner = false;

  public setId(param: number) {
    this.id = param;
  }
  public getId(): number {
    return this.id;
  }

  public getName(): string {
    return this.name;
  }
  public setName(param: string): void {
    this.name = param;
  }

  public getDiscriminator(): string {
    return this.discriminator;
  }
  public setDiscriminator(param: string) {
    this.discriminator = param;
  }

  public getCreatedBy(): User {
    return this.createdBy;
  }

  public setCreatedBy(createdBy: User) {
    this.createdBy = createdBy;
  }

  public getUpdatedBy(): User {
    return this.updatedBy;
  }

  public getCreatedAt(): Date {
    return this.createdAt;
  }

  public getUpdatedAt(): Date {
    return this.updatedAt;
  }

  public getType(): string {
    return this.type;
  }
  public setType(param: string): void {
    this.type = param;
  }

  public getSource(): string {
    return this.source;
  }
  public setSource(param: string): void {
    this.source = param;
  }

  public getStatus(): string {
    return this.status;
  }
  public setStatus(param: string): void {
    this.status = param;
  }

  public getInternalNote(): string {
    return this.internalNote;
  }
  public setInternalNote(param: string) {
    this.internalNote = param;
  }

  public getExternalNote(): string {
    return this.externalNote;
  }
  public setExternalNote(param: string): void {
    this.externalNote = param;
  }

  public getExternalNoteTranslations(): RateNoteTranslation[] {
    return this.externalNoteTranslations;
  }
  public setExternalNoteTranslations(param: RateNoteTranslation[]): void {
    this.externalNoteTranslations = param ? param : [];
  }

  public getExternalNoteForLocale(locale: string): string {
    const translation = this.externalNoteTranslations.find(
      (item) => item.locale === locale,
    );

    return translation ? translation.externalNote : "";
  }

  public setExternalNoteForLocale(locale: string, param: string): void {
    const translation = this.externalNoteTranslations.find(
      (item) => item.locale === locale,
    );

    if (translation) {
      translation.externalNote = param;

      return;
    }

    this.externalNoteTranslations.push(new RateNoteTranslation(locale, param));
  }

  public getPmNote(): string {
    return this.pmNote;
  }
  public setPmNote(param: string): void {
    this.pmNote = param;
  }

  public getComment(): string {
    return this.comment;
  }

  public setComment(param: string): void {
    this.comment = param;
  }

  public getDeepLink(): string {
    return this.deepLink;
  }
  public setDeepLink(param: string): void {
    this.deepLink = param;
  }

  public getCarrier(): Carrier {
    return this.carrier;
  }
  public setCarrier(param: Carrier): void {
    this.carrier = param;
  }

  public getAgent(): Client {
    return this.agent;
  }
  public setAgent(param: Client): void {
    this.agent = param;
  }

  public getValidity(): Date[] {
    return this.validity;
  }
  public setValidity(param: Date[]): void {
    this.validity = param;
  }

  public get validFrom(): Date {
    return this.validity ? this.validity[0] : null;
  }

  public get validTill(): Date {
    return this.validity ? this.validity[1] : null;
  }

  public get rateAndCarrierName(): string {
    return this.getName() + ", " + this.getCarrier().getName();
  }

  public getContractNumber(): string {
    return this.contractNumber;
  }

  public setContractNumber(param: string) {
    this.contractNumber = param;
  }

  public getRestrictedClients(): Client[] {
    return this.restrictedClients;
  }

  public setRestrictedClients(param: Client[]) {
    this.restrictedClients = param;
  }

  public getRestrictedUsers(): User[] {
    return this.restrictedUsers;
  }

  public setRestrictedUsers(param: User[]) {
    this.restrictedUsers = param;
  }

  public getRootParent(): Rate {
    return this.rootParent;
  }

  public getAvailableOnline(): boolean {
    return this.availableOnline;
  }

  public setAvailableOnline(param: boolean): void {
    this.availableOnline = param;
  }

  public getDefaultForInternalEmail(): boolean {
    return this.defaultForInternalEmail;
  }

  public setDefaultForInternalEmail(param: boolean): void {
    this.defaultForInternalEmail = param;
  }

  public getDefaultForExternalEmail(): boolean {
    return this.defaultForExternalEmail;
  }

  public setDefaultForExternalEmail(param: boolean): void {
    this.defaultForExternalEmail = param;
  }

  public getAvailableForPartner(): boolean {
    return this.availableForPartner;
  }

  public setAvailableForPartner(param: boolean): void {
    this.availableForPartner = param;
  }

  public isValid(): boolean {
    return !!this.name && !!this.carrier && !!this.validity;
  }

  public isExtendedRate(): boolean {
    return !!this.getRootParent();
  }

  public isSpotRate(): boolean {
    return this.getType() === rateType.spot;
  }

  public isExpired(): boolean {
    return !!this.validTill && isBefore(this.validTill, startOfDay(new Date()));
  }
}
